import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import KNForm from 'components/KNForm/KNForm'
import KNFormCheckbox from 'components/KNForm/KNFormCheckbox'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  updateNotificationPreferences,
  USER_PREFERENCES_QUERY_KEY,
  userPreferencesQueryOptions,
} from 'screens/UserManager/UserManager.service'
import type { NotificationsFormValues } from 'screens/UserManager/UserManager.types'

const Notifications = (): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<NotificationsFormValues>({
    defaultValues: processDefaultValues({
      wantsNewTripEmail: false,
      wantsUpdateTripEmail: false,
      wantsTripCancellationEmail: false,
    }),
    criteriaMode: 'all',
  })
  const queryClient = useQueryClient()
  const preferencesQuery = useQuery(userPreferencesQueryOptions())
  const updateNotificationPreferencesMutation = useMutation({
    mutationFn: updateNotificationPreferences,
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_QUERY_KEY] }),
  })

  useEffect(() => {
    if (preferencesQuery.data) {
      reset({
        wantsNewTripEmail: preferencesQuery.data.wantsNewTripEmail ?? false,
        wantsUpdateTripEmail: preferencesQuery.data.wantsUpdateTripEmail ?? false,
        wantsTripCancellationEmail: preferencesQuery.data.wantsTripCancellationEmail ?? false,
      })
    }
  }, [preferencesQuery.data])

  const onSubmit: SubmitHandler<NotificationsFormValues> = async (data: NotificationsFormValues) => {
    await updateNotificationPreferencesMutation.mutateAsync(data)
  }

  return (
    <Paper
      sx={{
        padding: 2,
      }}
    >
      <Typography variant="p2" mb={1} color="primary.main">
        {t('screens.shared.profile.email_notifications')}
      </Typography>
      <KNForm onSubmit={onSubmit}>
        <Stack direction="column" alignItems="start" spacing={1} mb={2}>
          <KNFormCheckbox
            name="wantsNewTripEmail"
            control={control}
            label={t('screens.shared.profile.new_trip_notification')}
          />
          <KNFormCheckbox
            name="wantsUpdateTripEmail"
            control={control}
            label={t('screens.shared.profile.trip_update_notification')}
          />
          <KNFormCheckbox
            name="wantsTripCancellationEmail"
            control={control}
            label={t('screens.shared.profile.trip_cancellation_notification')}
          />
        </Stack>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          size="small"
          disabled={!formState.isDirty}
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('general.save')}
        </Button>
      </KNForm>
    </Paper>
  )
}

export default Notifications
